import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import "./themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { getDvSourceImageUrl, ensureThemeJsonArray } from "../../../functions/themeAssets";

const FEEDBACK_TITLE_MAX = 40;
const FEEDBACK_MAIN_IMG = [400, 400];
const FEEDBACK_ITEM_IMG = [200, 200];
const MAX_FEEDBACK_ITEMS = 15;
const BELT_DIMS = [1938, 211];
const MOBILE_VIEW_DIMS = [414, 896];
const DESKTOP_VIEW_DIMS = [1920, 1080];
/** Shown in the game HUD next to timer and score (welcome page). */
const HUD_ICON_DIMS = [50, 50];

const TAB_GAME = "game";
const TAB_CONVEYOR = "conveyor";
const TAB_LAYOUT = "layout";
const TAB_HUD = "hud";

const apiUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

/** Avoid backend/S3 issues with spaces, parentheses, %, etc. in multipart original filenames. */
const pngFileWithBasename = (file, basename) =>
  new File([file], basename, {
    type: file.type || "image/png",
    lastModified: file.lastModified,
  });

const validateImageDimensions = (file, maxW, maxH) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img.width === maxW && img.height === maxH);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = URL.createObjectURL(file);
  });

const defaultFeedbackLayout = () => ({
  category1: { x: 10, y: 10, w: 20, h: 20 },
  category2: { x: 40, y: 10, w: 20, h: 20 },
  category3: { x: 70, y: 10, w: 20, h: 20 },
  /** Conveyor belt region (dice_image); same % coords as categories */
  belt: { x: 0, y: 86, w: 100 },
});

/** fetchThemeData JSON.parses row values, so layout may already be an object — not only a string. */
const safeParseLayout = (raw) => {
  if (raw != null && typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }
  if (!raw || typeof raw !== "string") return null;
  try {
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" && !Array.isArray(obj) ? obj : null;
  } catch {
    return null;
  }
};

const mergeLayout = (parsed) => {
  const d = defaultFeedbackLayout();
  if (!parsed) return d;
  return {
    category1: { ...d.category1, ...parsed.category1 },
    category2: { ...d.category2, ...parsed.category2 },
    category3: { ...d.category3, ...parsed.category3 },
    belt: { ...d.belt, ...(parsed.belt && typeof parsed.belt === "object" ? parsed.belt : {}) },
  };
};

const hashToTab = () => {
  const raw = (window.location.hash || "").replace(/^#/, "");
  if (raw === "tab5" || raw === "conveyor") return TAB_CONVEYOR;
  if (raw === "tab6" || raw === "layout") return TAB_LAYOUT;
  if (raw === "tab7" || raw === "hud") return TAB_HUD;
  if (raw === "tab3" || raw === "game" || raw === "categories" || raw === "tab-categories") return TAB_GAME;
  return TAB_GAME;
};

const tabToHash = (tab) => {
  if (tab === TAB_GAME) return "tab3";
  if (tab === TAB_CONVEYOR) return "tab5";
  if (tab === TAB_LAYOUT) return "tab6";
  if (tab === TAB_HUD) return "tab7";
  return "tab3";
};

function FeedbackLayoutEditor({
  adminToken,
  organizationId,
  sessionId,
  currentTheme,
  data,
  category1Image,
  category2Image,
  category3Image,
  onSaved,
}) {
  const stageRef = useRef(null);
  const layoutViewRef = useRef("desktop");
  const [layoutView, setLayoutView] = useState("desktop");
  const [layouts, setLayouts] = useState({
    desktop: defaultFeedbackLayout(),
    mobile: defaultFeedbackLayout(),
    mobileLandscape: defaultFeedbackLayout(),
  });
  const [loadingLocal, setLoadingLocal] = useState(false);
  const dragRef = useRef({ active: false, cat: null, startX: 0, startY: 0, origLeft: 0, origTop: 0 });
  const layoutsRef = useRef(layouts);
  layoutsRef.current = layouts;

  useEffect(() => {
    layoutViewRef.current = layoutView;
  }, [layoutView]);

  useEffect(() => {
    if (!data) return;
    setLayouts({
      desktop: mergeLayout(safeParseLayout(data.feedback_layout_desktop)),
      mobile: mergeLayout(safeParseLayout(data.feedback_layout_mobile)),
      mobileLandscape: mergeLayout(safeParseLayout(data.feedback_layout_mobile_landscape)),
    });
  }, [data]);

  const desktopBgUrl = useMemo(() => getDvSourceImageUrl(data?.desktop_view_image) || null, [data?.desktop_view_image]);
  const mobileBgUrl = useMemo(() => getDvSourceImageUrl(data?.mobile_view_image) || null, [data?.mobile_view_image]);
  const beltUrl = useMemo(() => getDvSourceImageUrl(data?.dice_image) || null, [data?.dice_image]);
  const catUrls = useMemo(
    () => [getDvSourceImageUrl(category1Image) || null, getDvSourceImageUrl(category2Image) || null, getDvSourceImageUrl(category3Image) || null],
    [category1Image, category2Image, category3Image]
  );

  const current = layouts[layoutView] || defaultFeedbackLayout();
  const bgUrl = layoutView === "mobile" ? mobileBgUrl : desktopBgUrl;
  const aspectRatio =
    layoutView === "mobile" ? "414 / 896" : "16 / 9";

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const setLayoutField = (fieldKey, partial) => {
    const lv = layoutViewRef.current;
    const defs = defaultFeedbackLayout();
    setLayouts((prev) => ({
      ...prev,
      [lv]: {
        ...prev[lv],
        [fieldKey]: { ...(defs[fieldKey] || {}), ...prev[lv][fieldKey], ...partial },
      },
    }));
  };

  const onPointerDown = (catKey) => (e) => {
    const lv = layoutViewRef.current;
    const defs = defaultFeedbackLayout();
    const el = layoutsRef.current[lv][catKey] || defs[catKey];
    dragRef.current = {
      active: true,
      cat: catKey,
      startX: e.clientX,
      startY: e.clientY,
      origLeft: el.x,
      origTop: el.y,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.active || !d.cat || !stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const dx = ((e.clientX - d.startX) / rect.width) * 100;
      const dy = ((e.clientY - d.startY) / rect.height) * 100;
      const lv = layoutViewRef.current;
      const defs = defaultFeedbackLayout();
      const wh = rect.width / rect.height;
      /** height/width of the asset; category mains are 400×400; belt matches BELT_DIMS */
      const hOverW = d.cat === "belt" ? BELT_DIMS[1] / BELT_DIMS[0] : 1;
      setLayouts((prev) => {
        const el = { ...defs[d.cat], ...prev[lv][d.cat] };
        const wPct = el.w ?? (d.cat === "belt" ? 100 : 20);
        const maxX = Math.max(0, 100 - wPct);
        const maxY = Math.max(0, 100 - wh * wPct * hOverW);
        return {
          ...prev,
          [lv]: {
            ...prev[lv],
            [d.cat]: {
              ...defs[d.cat],
              ...prev[lv][d.cat],
              x: clamp(d.origLeft + dx, 0, maxX),
              y: clamp(d.origTop + dy, 0, maxY),
            },
          },
        };
      });
    };
    const onUp = () => {
      dragRef.current = { active: false, cat: null, startX: 0, startY: 0, origLeft: 0, origTop: 0 };
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const saveLayout = async () => {
    const layout = layouts[layoutView];
    const key =
      layoutView === "desktop"
        ? "feedback_layout_desktop"
        : layoutView === "mobile"
          ? "feedback_layout_mobile"
          : "feedback_layout_mobile_landscape";
    try {
      setLoadingLocal(true);
      await updateThemeData({
        payload: {
          data: { [key]: JSON.stringify(layout) },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      Swal.fire("Success", "Layout saved", "success");
      await Promise.resolve(onSaved?.());
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed", "error");
    } finally {
      setLoadingLocal(false);
    }
  };

  const cats = ["category1", "category2", "category3"];

  return (
    <div className="themeupdate-layout-editor">
      <p className="themeupdate-muted">
        <code>
          Drag category images and the conveyor belt to position. Resize with sliders. Save separately for Desktop and
          Mobile portrait.
        </code>
      </p>
      <div className="themeupdate-layout-toolbar">
        <div className="themeupdate-btn-group">
          <button
            type="button"
            className={`themeupdate-btn ${layoutView === "desktop" ? "themeupdate-btn-active" : "themeupdate-btn-outline"}`}
            onClick={() => setLayoutView("desktop")}
          >
            Desktop
          </button>
          <button
            type="button"
            className={`themeupdate-btn ${layoutView === "mobile" ? "themeupdate-btn-active" : "themeupdate-btn-outline"}`}
            onClick={() => setLayoutView("mobile")}
          >
            Mobile portrait
          </button>
          <button
            type="button"
            className={`themeupdate-btn ${layoutView === "mobileLandscape" ? "themeupdate-btn-active" : "themeupdate-btn-outline"}`}
            onClick={() => setLayoutView("mobileLandscape")}
            style={{ display: "none" }}
            aria-hidden="true"
            tabIndex={-1}
          >
            Mobile landscape
          </button>
        </div>
        <button type="button" className="themeupdate-btn themeupdate-btn-success" onClick={saveLayout} disabled={loadingLocal}>
          Save layout
        </button>
      </div>

      <div className="themeupdate-layout-columns">
        <div
          ref={stageRef}
          className={`themeupdate-layout-stage${layoutView === "mobile" ? " themeupdate-layout-stage--mobile-portrait" : ""}`}
          style={{ aspectRatio }}
        >
          <div
            className="themeupdate-layout-bg"
            style={{
              backgroundImage: bgUrl ? `url('${bgUrl}')` : "none",
            }}
          />
          {cats.map((catKey, idx) => {
            const c = current[catKey];
            const url = catUrls[idx];
            if (!url) {
              return (
                <div
                  key={catKey}
                  className="themeupdate-layout-placeholder"
                  style={{ left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%` }}
                >
                  No category {idx + 1} image
                </div>
              );
            }
            return (
              <img
                key={catKey}
                alt={`Category ${idx + 1}`}
                src={url}
                className="themeupdate-layout-cat"
                style={{
                  left: `${c.x}%`,
                  top: `${c.y}%`,
                  width: `${c.w}%`,
                }}
                draggable={false}
                onPointerDown={onPointerDown(catKey)}
              />
            );
          })}
          {(() => {
            const b = current.belt || defaultFeedbackLayout().belt;
            if (!beltUrl) {
              return (
                <div
                  key="belt"
                  className="themeupdate-layout-placeholder themeupdate-layout-belt"
                  style={{ left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%` }}
                >
                  Upload a conveyor belt image on the Conveyor tab first
                </div>
              );
            }
            return (
              <img
                key="belt"
                alt="Conveyor belt"
                src={beltUrl}
                className="themeupdate-layout-cat themeupdate-layout-belt"
                style={{
                  left: `${b.x}%`,
                  top: `${b.y}%`,
                  width: `${b.w}%`,
                }}
                draggable={false}
                onPointerDown={onPointerDown("belt")}
              />
            );
          })()}
        </div>

        <div className="themeupdate-layout-side card-border">
          <h5 className="themeupdate-label">Resize</h5>
          {[1, 2, 3].map((n) => {
            const key = `category${n}`;
            const val = current[key]?.w ?? 20;
            return (
              <div key={key} className="themeupdate-form-group">
                <label className="themeupdate-switch-label">Category {n} size (%)</label>
                <input
                  type="range"
                  min={5}
                  max={60}
                  value={val}
                  className="themeupdate-range"
                  onChange={(e) => setLayoutField(key, { w: clamp(parseFloat(e.target.value) || 20, 5, 60) })}
                />
              </div>
            );
          })}
          <div className="themeupdate-form-group">
            <label className="themeupdate-switch-label">Conveyor belt width (%)</label>
            <input
              type="range"
              min={15}
              max={100}
              value={current.belt?.w ?? 100}
              className="themeupdate-range"
              onChange={(e) =>
                setLayoutField("belt", { w: clamp(parseFloat(e.target.value) || 100, 15, 100) })
              }
            />
          </div>
          <p className="themeupdate-muted">
            <code>Size controls width; height follows image aspect ratio.</code>
          </p>
        </div>
      </div>
    </div>
  );
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_GAME);

  const [fbTitles, setFbTitles] = useState({ 1: "", 2: "", 3: "" });
  const [fbItemFiles, setFbItemFiles] = useState({ 1: null, 2: null, 3: null });
  const [fbMainFiles, setFbMainFiles] = useState({ 1: null, 2: null, 3: null });
  const [beltFile, setBeltFile] = useState(null);
  const [mobileViewFile, setMobileViewFile] = useState(null);
  const [desktopViewFile, setDesktopViewFile] = useState(null);
  const [hudTimerFile, setHudTimerFile] = useState(null);
  const [hudPointsFile, setHudPointsFile] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);

  /** Native file inputs keep the chosen filename until `value` is cleared; clearing avoids duplicate uploads. */
  const fbMainInputRefs = useRef({});
  const fbItemsInputRefs = useRef({});
  const beltFileInputRef = useRef(null);
  const mobileViewInputRef = useRef(null);
  const desktopViewInputRef = useRef(null);
  const hudTimerInputRef = useRef(null);
  const hudPointsInputRef = useRef(null);
  const clearFileInput = (el) => {
    if (el) el.value = "";
  };

  /** After the user closes a validation/upload error alert, clear the related file input and state. */
  const swalUploadError = (message, resetSelection) =>
    Swal.fire("Error", message, "error").then(() => {
      resetSelection?.();
    });

  /** Same pattern as AddContent / updateThemeImage: POST /updateThemeMedia with DB field name as file input name. */
  const postUpdateThemeMedia = useCallback(
    async (file, fieldKey) => {
      const formData = new FormData();
      formData.append("themeName", currentTheme || "");
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName ?? data?.newthemeName ?? "",
          themeDescription: data?.themeDescription ?? "",
        })
      );
      formData.append(fieldKey, file);
      const res = await fetch(apiUrl("/updateThemeMedia"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `Upload failed (${res.status})`);
      }
      if (body.success === false && body.error) {
        throw new Error(body.error);
      }
      return body;
    },
    [adminToken, currentTheme, organizationId, sessionId, data]
  );

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setFbTitles({
      1: String(data.category1_title ?? ""),
      2: String(data.category2_title ?? ""),
      3: String(data.category3_title ?? ""),
    });
  }, [data]);

  useEffect(() => {
    const redirectTab = localStorage.getItem("redirectToTab");
    if (redirectTab) {
      localStorage.removeItem("redirectToTab");
      const n = String(redirectTab);
      if (n === "3") setActiveTab(TAB_GAME);
      else if (n === "5") setActiveTab(TAB_CONVEYOR);
      else if (n === "6") setActiveTab(TAB_LAYOUT);
      else if (n === "7") setActiveTab(TAB_HUD);
      return;
    }
    setActiveTab(hashToTab());
  }, []);

  useEffect(() => {
    const onHash = () => setActiveTab(hashToTab());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const goTab = (tab) => {
    setActiveTab(tab);
    window.location.hash = tabToHash(tab);
  };

  const saveFeedbackTitles = async () => {
    const t1 = (fbTitles[1] || "").trim().slice(0, FEEDBACK_TITLE_MAX);
    const t2 = (fbTitles[2] || "").trim().slice(0, FEEDBACK_TITLE_MAX);
    const t3 = (fbTitles[3] || "").trim().slice(0, FEEDBACK_TITLE_MAX);
    const emptyIdx = [t1, t2, t3].map((t, i) => (!t ? i + 1 : null)).filter(Boolean);
    if (emptyIdx.length) {
      const msg =
        emptyIdx.length === 3
          ? "Title cannot be empty. Please enter a title for each category."
          : emptyIdx.length === 1
            ? `Title cannot be empty. Please enter a title for category ${emptyIdx[0]}.`
            : `Title cannot be empty. Please enter titles for categories ${emptyIdx.join(", ")}.`;
      Swal.fire("", msg, "warning");
      return;
    }
    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            category1_title: t1,
            category2_title: t2,
            category3_title: t3,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Data updated", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save titles", "error");
    } finally {
      setLoading(false);
    }
  };

  const uploadFeedbackMainImage = async (categoryIndex) => {
    const resetMainSelection = () => {
      clearFileInput(fbMainInputRefs.current[categoryIndex]);
      setFbMainFiles((p) => ({ ...p, [categoryIndex]: null }));
    };
    const file = fbMainFiles[categoryIndex];
    if (!file) {
      swalUploadError("Choose a PNG file first.", resetMainSelection);
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      swalUploadError("Only PNG allowed.", resetMainSelection);
      return;
    }
    try {
      const ok = await validateImageDimensions(file, FEEDBACK_MAIN_IMG[0], FEEDBACK_MAIN_IMG[1]);
      if (!ok) {
        swalUploadError("Please upload an image with dimensions 400x400 px", resetMainSelection);
        return;
      }
    } catch {
      swalUploadError("Failed to validate image.", resetMainSelection);
      return;
    }
    const fieldKey = `category${categoryIndex}_image`;
    const safeMainName = `cat${categoryIndex}_main_${Date.now()}.png`;
    const fileToSend = pngFileWithBasename(file, safeMainName);
    setUploadBusy(true);
    try {
      await postUpdateThemeMedia(fileToSend, fieldKey);
      Swal.fire("Success", "Data updated", "success");
      clearFileInput(fbMainInputRefs.current[categoryIndex]);
      setFbMainFiles((p) => ({ ...p, [categoryIndex]: null }));
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (e) {
      swalUploadError(e.message || "Upload failed", resetMainSelection);
    } finally {
      setUploadBusy(false);
    }
  };

  const feedbackItemsCount = (idx) => ensureThemeJsonArray(data?.[`category${idx}_items`]).length;

  const uploadFeedbackItems = async (categoryIndex) => {
    const resetItemsSelection = () => {
      clearFileInput(fbItemsInputRefs.current[categoryIndex]);
      setFbItemFiles((p) => ({ ...p, [categoryIndex]: null }));
    };
    const input = fbItemFiles[categoryIndex];
    if (!input || !input.length) {
      swalUploadError("Select at least one image.", resetItemsSelection);
      return;
    }
    const files = Array.from(input);
    const existing = feedbackItemsCount(categoryIndex);
    if (existing + files.length > MAX_FEEDBACK_ITEMS) {
      swalUploadError(
        `Maximum ${MAX_FEEDBACK_ITEMS} images. You have ${existing} and selected ${files.length} more.`,
        resetItemsSelection
      );
      return;
    }
    for (const file of files) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext !== "png") {
        swalUploadError("Only PNG allowed for items.", resetItemsSelection);
        return;
      }
      try {
        const ok = await validateImageDimensions(file, FEEDBACK_ITEM_IMG[0], FEEDBACK_ITEM_IMG[1]);
        if (!ok) {
          swalUploadError("Please upload an image with dimensions 200×200 px", resetItemsSelection);
          return;
        }
      } catch {
        swalUploadError("Failed to validate images.", resetItemsSelection);
        return;
      }
    }
    const fd = new FormData();
    fd.append("themeName", currentTheme || "");
    fd.append("category_index", String(categoryIndex));
    fd.append("organizationId", organizationId || "admin");
    fd.append("sessionId", sessionId || "admin");
    const batchTs = Date.now();
    files.forEach((f, i) => {
      const safeName = `fb-c${categoryIndex}-${batchTs}-${i}.png`;
      fd.append("files", pngFileWithBasename(f, safeName));
    });
    setUploadBusy(true);
    try {
      const res = await fetch(apiUrl("/mergeFeedbackCategoryItems"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j.success) {
        Swal.fire("Success", "Successfully updated", "success");
        clearFileInput(fbItemsInputRefs.current[categoryIndex]);
        setFbItemFiles((p) => ({ ...p, [categoryIndex]: null }));
        dispatch(fetchThemeData({ themeId: currentTheme }));
      } else {
        swalUploadError(j.error || "Upload failed", resetItemsSelection);
      }
    } catch {
      swalUploadError("Upload failed", resetItemsSelection);
    } finally {
      setUploadBusy(false);
    }
  };

  const deleteFeedbackItem = async (categoryIndex, filename) => {
    const key = `category${categoryIndex}_items`;
    const next = ensureThemeJsonArray(data?.[key]).filter((x) => x !== filename);
    setUploadBusy(true);
    try {
      await updateThemeData({
        payload: {
          data: { [key]: next },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Deleted", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Delete failed", "error");
    } finally {
      setUploadBusy(false);
    }
  };

  const uploadConveyorBelt = async (e) => {
    e.preventDefault();
    const resetBeltSelection = () => {
      clearFileInput(beltFileInputRef.current);
      setBeltFile(null);
    };
    if (!beltFile) {
      swalUploadError("Choose a PNG file.", resetBeltSelection);
      return;
    }
    const ext = beltFile.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      swalUploadError("Only PNG allowed.", resetBeltSelection);
      return;
    }
    try {
      const ok = await validateImageDimensions(beltFile, BELT_DIMS[0], BELT_DIMS[1]);
      if (!ok) {
        swalUploadError("Please upload an image with dimensions 1938x211 px", resetBeltSelection);
        return;
      }
    } catch {
      swalUploadError("Failed to validate image.", resetBeltSelection);
      return;
    }
    if (beltFile.size > 5 * 1024 * 1024) {
      swalUploadError("Max file size 5 MB.", resetBeltSelection);
      return;
    }
    setUploadBusy(true);
    try {
      await postUpdateThemeMedia(beltFile, "dice_image");
      Swal.fire("Success", "Successfully updated", "success");
      clearFileInput(beltFileInputRef.current);
      setBeltFile(null);
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (e) {
      swalUploadError(e.message || "Upload failed", resetBeltSelection);
    } finally {
      setUploadBusy(false);
    }
  };

  const uploadViewImage = async (e, viewType, file, setFile) => {
    e.preventDefault();
    const resetViewSelection = () => {
      clearFileInput(viewType === "mobile" ? mobileViewInputRef.current : desktopViewInputRef.current);
      setFile(null);
    };
    if (!file) {
      swalUploadError("Choose a PNG file.", resetViewSelection);
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      swalUploadError("Only PNG allowed.", resetViewSelection);
      return;
    }
    const dims = viewType === "mobile" ? MOBILE_VIEW_DIMS : DESKTOP_VIEW_DIMS;
    try {
      const ok = await validateImageDimensions(file, dims[0], dims[1]);
      if (!ok) {
        swalUploadError(`Please upload an image with dimensions ${dims[0]}×${dims[1]} px.`, resetViewSelection);
        return;
      }
    } catch {
      swalUploadError("Failed to validate image.", resetViewSelection);
      return;
    }
    const fieldKey = viewType === "mobile" ? "mobile_view_image" : "desktop_view_image";
    const safeViewName = viewType === "mobile" ? `mobile_view_${Date.now()}.png` : `desktop_view_${Date.now()}.png`;
    const fileToSend = pngFileWithBasename(file, safeViewName);
    setUploadBusy(true);
    try {
      await postUpdateThemeMedia(fileToSend, fieldKey);
      Swal.fire("Success", "Data updated", "success");
      clearFileInput(viewType === "mobile" ? mobileViewInputRef.current : desktopViewInputRef.current);
      setFile(null);
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (e) {
      swalUploadError(e.message || "Upload failed", resetViewSelection);
    } finally {
      setUploadBusy(false);
    }
  };

  const uploadHudIcon = async (e, kind, file, setFile) => {
    e.preventDefault();
    const resetHudSelection = () => {
      clearFileInput(kind === "timer" ? hudTimerInputRef.current : hudPointsInputRef.current);
      setFile(null);
    };
    if (!file) {
      swalUploadError("Choose a PNG file.", resetHudSelection);
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      swalUploadError("Only PNG allowed.", resetHudSelection);
      return;
    }
    try {
      const ok = await validateImageDimensions(file, HUD_ICON_DIMS[0], HUD_ICON_DIMS[1]);
      if (!ok) {
        swalUploadError("Please upload an image with dimensions 50x50 px", resetHudSelection);
        return;
      }
    } catch {
      swalUploadError("Failed to validate image.", resetHudSelection);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      swalUploadError("Max file size 5 MB.", resetHudSelection);
      return;
    }
    const fieldKey = kind === "timer" ? "hud_timer_image" : "hud_points_image";
    setUploadBusy(true);
    try {
      await postUpdateThemeMedia(file, fieldKey);
      Swal.fire("Success", "Data updated", "success");
      clearFileInput(kind === "timer" ? hudTimerInputRef.current : hudPointsInputRef.current);
      setFile(null);
      dispatch(fetchThemeData({ themeId: currentTheme }));
    } catch (err) {
      swalUploadError(err.message || "Upload failed", resetHudSelection);
    } finally {
      setUploadBusy(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  const renderFeedbackCategory = (idx) => {
    const title = fbTitles[idx] ?? "";
    const mainKey = `category${idx}_image`;
    const itemsKey = `category${idx}_items`;
    const mainName = data?.[mainKey];
    const items = ensureThemeJsonArray(data?.[itemsKey]);
    return (
      <div key={idx} className="themeupdate-feedback-card">
        <div className="themeupdate-card-header">Category {idx}</div>
        <div className="themeupdate-card-body">
          <div className="themeupdate-feedback-category-title-block">
            <label className="themeupdate-label" htmlFor={`fb_title_${idx}`}>
              Title
            </label>
            <input
              id={`fb_title_${idx}`}
              type="text"
              className="themeupdate-input"
              maxLength={FEEDBACK_TITLE_MAX}
              value={title}
              onChange={(e) => setFbTitles((p) => ({ ...p, [idx]: e.target.value }))}
            />
          </div>
          <label className="themeupdate-label">Current image</label>
          <div className="themeupdate-feedback-preview-main">
            {mainName ? (
              <img src={getDvSourceImageUrl(mainName)} alt={`Category ${idx}`} className="themeupdate-feedback-main-img" />
            ) : (
              <p className="themeupdate-muted">No image set.</p>
            )}
          </div>
          <div className="themeupdate-file-wrap">
            <input
              ref={(el) => {
                fbMainInputRefs.current[idx] = el;
              }}
              type="file"
              accept="image/png"
              onChange={(e) => setFbMainFiles((p) => ({ ...p, [idx]: e.target.files?.[0] || null }))}
            />
          </div>
          <button
            type="button"
            className="themeupdate-btn themeupdate-btn-success "
            disabled={uploadBusy}
            onClick={() => uploadFeedbackMainImage(idx)}
          >
            <i className="fa-solid fa-upload"></i> Upload
          </button>

          <hr className="themeupdate-hr" />
          <label className="themeupdate-label">Items</label>
          <p className="themeupdate-muted">
            <code>PNG only, 200×200, max {MAX_FEEDBACK_ITEMS} images</code>
          </p>
          <div className="themeupdate-feedback-items-grid">
            {items.length === 0 ? (
              <span className="themeupdate-muted">No items uploaded yet.</span>
            ) : (
              items.map((img) => (
                <div key={img} className="themeupdate-feedback-item-tile">
                  <img src={getDvSourceImageUrl(img)} alt="" />
                  <button
                    type="button"
                    className="themeupdate-feedback-item-remove"
                    aria-label="Remove"
                    disabled={uploadBusy}
                    onClick={() => deleteFeedbackItem(idx, img)}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="themeupdate-file-wrap">
            <input
              ref={(el) => {
                fbItemsInputRefs.current[idx] = el;
              }}
              type="file"
              accept="image/png"
              multiple
              onChange={(e) => setFbItemFiles((p) => ({ ...p, [idx]: e.target.files }))}
            />
          </div>
          <button
            type="button"
            className="themeupdate-btn themeupdate-btn-success "
            disabled={uploadBusy}
            onClick={() => uploadFeedbackItems(idx)}
          >
            Upload items
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="themeupdate-tabs">
        <button
          type="button"
          className={`themeupdate-tab ${activeTab === TAB_GAME ? "active" : ""}`}
          onClick={() => goTab(TAB_GAME)}
        >
          <i className="fa-solid fa-sliders"></i> Game settings
        </button>
        <button
          type="button"
          className={`themeupdate-tab ${activeTab === TAB_CONVEYOR ? "active" : ""}`}
          onClick={() => goTab(TAB_CONVEYOR)}
        >
          <i className="fa-solid fa-image"></i> Conveyor belt images
        </button>
        <button
          type="button"
          className={`themeupdate-tab ${activeTab === TAB_LAYOUT ? "active" : ""}`}
          onClick={() => goTab(TAB_LAYOUT)}
        >
          <i className="fa-solid fa-border-all"></i> Layout editor
        </button>
        <button
          type="button"
          className={`themeupdate-tab ${activeTab === TAB_HUD ? "active" : ""}`}
          onClick={() => goTab(TAB_HUD)}
        >
          <i className="fa-solid fa-stopwatch"></i> Timer &amp; points icons
        </button>
      </div>

      {activeTab === TAB_GAME && (
        <div className="themeupdate-panel-wide">
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Feedback categories (1–3)</div>
            <div className="themeupdate-card-body">
              <p className="themeupdate-muted">
                <code>Main image (PNG 400×400) + item images (PNG 200×200)</code>
              </p>
              <div className="themeupdate-feedback-grid">{[1, 2, 3].map((i) => renderFeedbackCategory(i))}</div>
              <div className="themeupdate-actions themeupdate-feedback-save-titles">
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-success"
                  onClick={saveFeedbackTitles}
                  disabled={loading || uploadBusy}
                >
                  Save titles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === TAB_CONVEYOR && (
        <div className="themeupdate-panel-wide">
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Conveyor belt image</div>
            <div className="themeupdate-card-body">
              <p className="themeupdate-muted">
                <code>
                  PNG only · one image · {BELT_DIMS[0]} × {BELT_DIMS[1]} px · max 5 MB
                </code>
              </p>
              <form onSubmit={uploadConveyorBelt} className="themeupdate-upload-form">
                <div className="themeupdate-file-wrap">
                  <input
                    ref={beltFileInputRef}
                    type="file"
                    accept="image/png"
                    onChange={(e) => setBeltFile(e.target.files?.[0] || null)}
                  />
                </div>
                <button type="submit" className="themeupdate-btn themeupdate-btn-success" disabled={uploadBusy}>
                  Upload belt
                </button>
              </form>
              <div className="themeupdate-conveyor-preview">
                {data?.dice_image ? (
                  <img src={getDvSourceImageUrl(data.dice_image)} alt="Conveyor belt" className="themeupdate-preview-fluid" />
                ) : (
                  <p className="themeupdate-muted">No conveyor belt image uploaded yet.</p>
                )}
              </div>

              <hr className="themeupdate-hr" />

              <div className="themeupdate-view-upload-row">
                <div className="themeupdate-view-upload-col">
                  <h4 className="themeupdate-label">Mobile view background</h4>
                  <p className="themeupdate-muted">
                    <code>
                      PNG · {MOBILE_VIEW_DIMS[0]} × {MOBILE_VIEW_DIMS[1]} px
                    </code>
                  </p>
                  <form
                    onSubmit={(e) => uploadViewImage(e, "mobile", mobileViewFile, setMobileViewFile)}
                    className="themeupdate-upload-form"
                  >
                    <div className="themeupdate-file-wrap">
                      <input
                        ref={mobileViewInputRef}
                        type="file"
                        accept="image/png"
                        onChange={(e) => setMobileViewFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <button type="submit" className="themeupdate-btn themeupdate-btn-success" disabled={uploadBusy}>
                      Upload
                    </button>
                  </form>
                  {data?.mobile_view_image ? (
                    <img src={getDvSourceImageUrl(data.mobile_view_image)} alt="Mobile" className="themeupdate-preview-fluid" />
                  ) : (
                    <p className="themeupdate-muted">No mobile view image yet.</p>
                  )}
                </div>
                <div className="themeupdate-view-upload-col">
                  <h4 className="themeupdate-label">Desktop view background</h4>
                  <p className="themeupdate-muted">
                    <code>
                      PNG · {DESKTOP_VIEW_DIMS[0]} × {DESKTOP_VIEW_DIMS[1]} px
                    </code>
                  </p>
                  <form
                    onSubmit={(e) => uploadViewImage(e, "desktop", desktopViewFile, setDesktopViewFile)}
                    className="themeupdate-upload-form"
                  >
                    <div className="themeupdate-file-wrap">
                      <input
                        ref={desktopViewInputRef}
                        type="file"
                        accept="image/png"
                        onChange={(e) => setDesktopViewFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <button type="submit" className="themeupdate-btn themeupdate-btn-success" disabled={uploadBusy}>
                      Upload
                    </button>
                  </form>
                  {data?.desktop_view_image ? (
                    <img src={getDvSourceImageUrl(data.desktop_view_image)} alt="Desktop" className="themeupdate-preview-fluid" />
                  ) : (
                    <p className="themeupdate-muted">No desktop view image yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === TAB_HUD && (
        <div className="themeupdate-panel-wide">
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Game HUD icons</div>
            <div className="themeupdate-card-body">
              <p className="themeupdate-muted">
                Icons next to the timer and score on the welcome / play screen.{" "}
                <code>
                  PNG · {HUD_ICON_DIMS[0]} × {HUD_ICON_DIMS[1]} px · max 5 MB
                </code>
              </p>
              <div className="themeupdate-view-upload-row">
                <div className="themeupdate-view-upload-col">
                  <h4 className="themeupdate-label">Timer icon</h4>
                  <form
                    onSubmit={(e) => uploadHudIcon(e, "timer", hudTimerFile, setHudTimerFile)}
                    className="themeupdate-upload-form"
                  >
                    <div className="themeupdate-file-wrap">
                      <input
                        ref={hudTimerInputRef}
                        type="file"
                        accept="image/png"
                        onChange={(e) => setHudTimerFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <button type="submit" className="themeupdate-btn themeupdate-btn-success" disabled={uploadBusy}>
                      Upload
                    </button>
                  </form>
                  {data?.hud_timer_image ? (
                    <img src={getDvSourceImageUrl(data.hud_timer_image)} alt="Timer icon" className="themeupdate-preview-fluid" />
                  ) : (
                    <p className="themeupdate-muted">Using default timer icon until you upload.</p>
                  )}
                </div>
                <div className="themeupdate-view-upload-col">
                  <h4 className="themeupdate-label">Points icon</h4>
                  <form
                    onSubmit={(e) => uploadHudIcon(e, "points", hudPointsFile, setHudPointsFile)}
                    className="themeupdate-upload-form"
                  >
                    <div className="themeupdate-file-wrap">
                      <input
                        ref={hudPointsInputRef}
                        type="file"
                        accept="image/png"
                        onChange={(e) => setHudPointsFile(e.target.files?.[0] || null)}
                      />
                    </div>
                    <button type="submit" className="themeupdate-btn themeupdate-btn-success" disabled={uploadBusy}>
                      Upload
                    </button>
                  </form>
                  {data?.hud_points_image ? (
                    <img src={getDvSourceImageUrl(data.hud_points_image)} alt="Points icon" className="themeupdate-preview-fluid" />
                  ) : (
                    <p className="themeupdate-muted">Using default points icon until you upload.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === TAB_LAYOUT && data && (
        <div className="themeupdate-panel-wide">
          <div className="themeupdate-card">
            <div className="themeupdate-card-header">Place category images on background</div>
            <div className="themeupdate-card-body">
              <FeedbackLayoutEditor
                adminToken={adminToken}
                organizationId={organizationId}
                sessionId={sessionId}
                currentTheme={currentTheme}
                data={data}
                category1Image={data.category1_image}
                category2Image={data.category2_image}
                category3Image={data.category3_image}
                onSaved={() => dispatch(fetchThemeData({ themeId: currentTheme })).unwrap()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeUpdate;
